import React, { FunctionComponent } from 'react';

import { connect } from 'react-redux';

import Dinero from 'dinero.js';

// Store
import AppState from '@webapp/store/state';

// Translate
import { Translate } from 'react-localize-redux';

// Styled
import styled from 'styled-components';

// Components
import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';
import UnselectedIcon from '@components/basics/icons/unselected';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import ErrorMessage from '@components/basics/error-message';

import {
  cleanValue,
  serializeAmount,
} from '@webapp/store/stripe-connect/utils';

import {
  MIN_US_TRANSFER_OBJECT,
  MIN_INTL_TRANSFER_OBJECT,
} from '@services/stripe-connect/constants';
import { isAccountFromCountry } from '@services/stripe-connect/lib/is-account-from-country';
import { ConnectedAccount } from '@services/stripe-connect/types/connected-account';

interface WithdrawMenuContentProps {
  className?: string;
  isMobile?: boolean;
  account?: ConnectedAccount;
  available: Dinero.Dinero;
  errorMsg?: string;

  txAmount: string;
  onChange: (value: string) => void;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
  account: state.stripeConnect.account,
  available: state.royalties.balance,
});

const WithdrawMenuContent: FunctionComponent<WithdrawMenuContentProps> = ({
  className,
  errorMsg,
  account,
  txAmount,
  available,
  onChange,
}) => {
  const allFunds = serializeAmount(`${txAmount}`).equalsTo(available);

  const handleValueChange = (value: string) => {
    const money = serializeAmount(value);
    if (money.isNegative()) {
      return;
    }
    if (money.greaterThan(available)) {
      return;
    }

    onChange(cleanValue(value));
  };

  const getError = () => {
    if (!account) {
      return <Translate id="PAYOUT.NO_ACCOUNT" />;
    }
    if (txAmount) {
      const money = serializeAmount(txAmount);
      if (isAccountFromCountry(account, 'US')) {
        if (money.lessThan(Dinero(MIN_US_TRANSFER_OBJECT))) {
          return <Translate id="PAYOUT.MIN_US_AMOUNT" />;
        }
      } else if (isAccountFromCountry(account, 'NG')) {
        if (money.lessThan(Dinero(MIN_US_TRANSFER_OBJECT))) {
          return <Translate id="PAYOUT.MIN_US_AMOUNT" />;
        }
      } else {
        if (money.lessThan(Dinero(MIN_INTL_TRANSFER_OBJECT))) {
          return <Translate id="PAYOUT.MIN_INTL_AMOUNT" />;
        }
      }
      if (money.greaterThan(available)) {
        return <Translate id="PAYOUT.MAX_AMOUNT" />;
      }
    }
  };

  const getErrorMessage = (errorId) => {
    switch (errorId) {
      case 'validation.invalidValueForField':
      case 'validation.snapx.invalidPaymentMethod':
        return 'TRANSFER.ACCOUNT.INVALID';
      case 'validation.snapx.min':
      case 'validation.snapx.transactionAmountTooSmall':
        return 'TRANSFER.MIN.AMOUNT';
      case 'wy.validation.error':
        return;
      default:
        return 'transfer.insufficientFunds';
    }
  };

  return (
    <Content className={className || ''}>
      {!errorMsg ? (
        <>
          <CheckboxBox className={allFunds ? 'selected' : ''} noBorderBottom>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              className="mb-20"
            >
              <Typography type="section-title" customFont="medium">
                <Translate id="USER_PROFILE.WALLET.WITHDRAW.MENU.AMOUNT.TITLE" />
              </Typography>
              <Flex
                alignItems="center"
                justifyContent="flex-end"
                gap={10}
                onClick={() =>
                  onChange(available.toFormat('0.00', 'HALF_DOWN'))
                }
                style={{ cursor: 'pointer' }}
              >
                <Typography type="small" weight="700">
                  <Translate id="USER_PROFILE.WALLET.WITHDRAW.MENU.AMOUNT.ALL_FUNDS" />
                </Typography>
                {allFunds ? (
                  <CompletedIcon className="icon" />
                ) : (
                  <UnselectedIcon className="icon" />
                )}
              </Flex>
            </Flex>
            <InputField
              label={
                <Translate id="USER_PROFILE.WALLET.WITHDRAW.MENU.AMOUNT.INPUT.LABEL" />
              }
              value={txAmount ? `$${txAmount}` : ''}
              placeholder="$0"
              onChange={handleValueChange}
              max={available.getAmount()}
              error={getError()}
              rightDecoration={
                <InputDecoration>
                  <Typography type="smaller" weight="700">
                    <Translate id="USER_PROFILE.WALLET.WITHDRAW.MENU.AMOUNT.INPUT.DECORATION" />
                  </Typography>
                  <Typography type="small">{available.toFormat()}</Typography>
                </InputDecoration>
              }
            />
          </CheckboxBox>
        </>
      ) : null}
      {errorMsg ? (
        <Link
          href="https://support.trac.co/hc/en-us/requests/new?ticket_form_id=1900000059787"
          target="_blank"
        >
          <ErrorMessage
            hasBackground
            className="mt-20 mb-20"
            errorId={getErrorMessage(errorMsg) || 'PAYOUT.DEFAULT_TO_FALLBACK'}
            fallback={
              'We are unable to process your request. Click here to contact support'
            }
          />
        </Link>
      ) : null}
    </Content>
  );
};

export default connect(mapStateToProps)(WithdrawMenuContent);

const Link = styled.a`
  color: inherit;
  text-decoration: none;
`;

const Content = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const CheckboxBox = styled(Box)`
  width: 100%;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &.selected {
    background-color: ${({ theme }) => theme.colors.primary};
    span {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }

  .icon {
    width: 24px;
    height: 24px;
  }
`;

const InputDecoration = styled.div`
  text-align: right;
  span {
    color: ${({ theme }) => theme.colors.primary} !important;
  }
`;
